import {
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type Ref,
} from 'react';
import {
  Background,
  Controls,
  MiniMap,
  ReactFlow,
  ReactFlowProvider,
  useStore,
  type Node,
  type NodeChange,
  type NodeMouseHandler,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useTheme } from '../../hooks/useTheme';
import { CanvasToolbar, type FullscreenControl } from './CanvasToolbar';
import { FullscreenHold } from './FullscreenHold';
import { canCollapse, type ViewEdge, type ViewNode } from './graphModel';
import { GraphHelp } from './GraphHelp';
import { minimapFill } from './kindPalette';
import { motionDuration } from './motion';
import { createNodeDataCache } from './nodeDataCache';
import { nodeTypes } from './nodes';
import { Reframe, type ViewportRequest } from './Reframe';
import { useCentreOn } from './useCentreOn';
import { useGraphHelp } from './useGraphHelp';
import type { ViewMode } from './viewUrl';
import { cardMentions, edgesToDraw, useGraphLayout, type FlowNodeData } from './useGraphLayout';
import { selectOverview } from './zoomLevel';

export type { ViewportRequest } from './Reframe';

/** What the page can ask of the canvas from outside its React Flow store. */
export type GraphCanvasHandle = {
  /** What Centre on focus does: pans to the root at zoom 1. */
  centreOnFocus: () => void;
};

type Props = {
  nodes: ViewNode[];
  /** Every visible edge; all place the nodes on the rings, and all are drawn above overview zoom. */
  edges: ViewEdge[];
  /** The edges that place a node (`VisibleGraph.treeEdges`): drawn at full strength, and alone when zoomed out. */
  treeEdgeIds: ReadonlySet<string>;
  /** Visible nodes with at least one child placed under them. */
  parentIds: ReadonlySet<string>;
  /** The documents that mention each entity (`mentionCounts`); shown on the entity cards. */
  mentionCounts: ReadonlyMap<string, number>;
  /** The tree edges on the selection's path to the root; drawn emphasised. */
  pathEdgeIds: ReadonlySet<string>;
  /** The root of the view: it shows its children and Centre on focus pans to it. Always in `nodes`. */
  rootId: string;
  /** The rings are laid out around this node; it is always in `nodes`. */
  centreId: string;
  selectedId: string | null;
  expandedIds: ReadonlySet<string>;
  expandableIds: ReadonlySet<string>;
  viewportRequest: ViewportRequest | null;
  /** A click, Enter, or Space on a node: select it and show its children. Stable across renders. */
  onActivate: (id: string) => void;
  /** The node's collapse button. Stable across renders. */
  onCollapse: (id: string) => void;
  /** An expanded group's Show 30 more button. Stable across renders. */
  onShowMore: (id: string) => void;
  /** A click on the empty canvas, or Escape in it: clear the selection. */
  onDeselect: () => void;
  onOpenDocument: (reconstructedDocumentId: string) => void;
  /** The toolbar's CSV button: downloads the visible relationships. */
  onExportCsv: () => void;
  /** The toolbar's Graph / List control. */
  onMode: (mode: ViewMode) => void;
  /** The full screen button's state and toggle; absent where the page may not go full screen. */
  fullscreen?: FullscreenControl;
  ref?: Ref<GraphCanvasHandle>;
};

const ACTIVATE_KEYS = new Set(['Enter', ' ']);

/** The `mentions` field of an entity card's data; other cards carry none. */
const withMentions = (mentions: number | undefined) => (mentions === undefined ? {} : { mentions });

/** The id of the node whose wrapper `target` is; `null` for any other element (a control inside a card). */
const nodeIdOf = (target: EventTarget): string | null =>
  target instanceof HTMLElement && target.classList.contains('react-flow__node') ? (target.dataset.id ?? null) : null;

/** The minimap fills each node with its kind's compact colour. */
const minimapColor = (node: Node<FlowNodeData>) => minimapFill(node.data.node.kind);

/**
 * React Flow's own surfaces in the app's colours (see index.css): the canvas zinc-900 in dark, so the focus
 * ring's offset matches it; the minimap and the zoom buttons as elevated zinc-800 panels with zinc-700 borders.
 * The colour scheme follows the app's `dark` class, which the minimap's `light-dark()` fills read.
 */
const CANVAS_THEME = [
  '[color-scheme:light] dark:[color-scheme:dark]',
  '[--xy-controls-button-border-color:var(--color-gray-200)]',
  'dark:[--xy-background-color:var(--color-zinc-900)]',
  'dark:[--xy-minimap-background-color:var(--color-zinc-800)]',
  'dark:[--xy-minimap-mask-background-color:color-mix(in_oklab,var(--color-zinc-900)_60%,transparent)]',
  'dark:[--xy-controls-button-background-color:var(--color-zinc-800)]',
  'dark:[--xy-controls-button-background-color-hover:var(--color-zinc-700)]',
  'dark:[--xy-controls-button-border-color:var(--color-zinc-700)]',
].join(' ');

function Overlay({ children }: { children: string }) {
  return (
    <div className="pointer-events-none absolute inset-0 z-10 grid place-items-center text-sm text-gray-500 dark:text-zinc-400">
      {children}
    </div>
  );
}

/** The canvas in its own React Flow store, so the canvas itself can read the zoom. */
export function GraphCanvas(props: Props) {
  return (
    <ReactFlowProvider>
      <Canvas {...props} />
    </ReactFlowProvider>
  );
}

function Canvas({
  nodes,
  edges,
  treeEdgeIds,
  parentIds,
  mentionCounts,
  pathEdgeIds,
  rootId,
  centreId,
  selectedId,
  expandedIds,
  expandableIds,
  viewportRequest,
  onActivate,
  onCollapse,
  onShowMore,
  onDeselect,
  onOpenDocument,
  onExportCsv,
  onMode,
  fullscreen,
  ref,
}: Props) {
  const { mode } = useTheme();
  const { positioned, targets, flowEdges, error, layoutKey, positions, moveMs, mover } = useGraphLayout(
    nodes,
    edges,
    treeEdgeIds,
    centreId,
  );
  const overview = useStore(selectOverview);
  const rootTarget = useMemo(() => targets.find((n) => n.id === rootId), [targets, rootId]);
  const help = useGraphHelp();
  const centreOn = useCentreOn();
  const centreOnFocus = () => void centreOn(rootTarget, { zoom: 1, duration: motionDuration(300) });
  useImperativeHandle(ref, () => ({ centreOnFocus }));
  // The size React Flow measured for each card. A node object without one makes React Flow drop the card's handle
  // places until it measures again, so an edge would jump off its card while the nodes move; carrying the size
  // keeps them.
  const [measured, setMeasured] = useState<ReadonlyMap<string, { width: number; height: number }>>(() => new Map());
  const nodesChange = useCallback((changes: NodeChange<Node<FlowNodeData>>[]) => {
    const sizes = changes.flatMap((c) =>
      c.type === 'dimensions' && c.dimensions ? [[c.id, c.dimensions] as const] : [],
    );
    if (sizes.length === 0) return;
    setMeasured((prev) => {
      const changed = sizes.filter(
        ([id, size]) => prev.get(id)?.width !== size.width || prev.get(id)?.height !== size.height,
      );
      return changed.length === 0 ? prev : new Map([...prev, ...changed]);
    });
  }, []);
  // Selection and expansion change without moving any node; overlay them here so no re-layout runs. Built from
  // the layout targets, not the drawn positions, and kept per node while its fields are the same, so a frame of a
  // move (or an unrelated parent render such as a search keystroke) hands every memoised card the same data.
  // Only a node `canCollapse` allows gets a collapse control; only a group with members left, Show 30 more.
  const [dataCache] = useState(createNodeDataCache);
  const dataById = useMemo(() => {
    const data = dataCache.reuse(
      targets.map((n) => ({
        node: n.data.node,
        ...withMentions(cardMentions(n.data.node, mentionCounts)),
        selected: n.id === selectedId,
        focused: n.id === rootId,
        expanded: n.id === rootId || expandedIds.has(n.id),
        // The root always shows its children, so it draws no chevron (as ProjectNode does).
        expandable: n.id !== rootId && expandableIds.has(n.id),
        ...(canCollapse(n.data.node, rootId, expandedIds, parentIds) ? { onCollapse } : {}),
        ...(n.data.node.kind === 'group' && n.data.node.remaining > 0 ? { onShowMore } : {}),
      })),
    );
    return new Map(data.map((d) => [d.node.id, d]));
  }, [
    dataCache,
    targets,
    selectedId,
    rootId,
    expandedIds,
    expandableIds,
    parentIds,
    mentionCounts,
    onCollapse,
    onShowMore,
  ]);
  // Only `position` (and `measured`, once React Flow reports it) changes between frames of a move.
  const shown = useMemo(
    () =>
      positioned.map((n) => ({
        ...n,
        ...(measured.has(n.id) ? { measured: measured.get(n.id) } : {}),
        data: dataById.get(n.id) ?? n.data,
      })),
    [positioned, measured, dataById],
  );
  // The node under the pointer or with keyboard focus: its edges are drawn emphasised. One that left the view
  // (a collapse removes a card without a mouse leave) emphasises nothing.
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const emphasisedId = hoveredId !== null && dataById.has(hoveredId) ? hoveredId : null;
  // Keyed on the overview boolean, not the zoom, so a pan or a zoom step never maps the edges again; and on the
  // hovered id, which a mouse move over the same card leaves as it is.
  const drawnEdges = useMemo(
    () => edgesToDraw(flowEdges, treeEdgeIds, pathEdgeIds, overview, emphasisedId),
    [flowEdges, treeEdgeIds, pathEdgeIds, overview, emphasisedId],
  );
  const enterNode: NodeMouseHandler<Node<FlowNodeData>> = (_event, node) => setHoveredId(node.id);
  const leaveNode = () => setHoveredId(null);
  const focusNode = (event: FocusEvent<HTMLDivElement>) => {
    const id = nodeIdOf(event.target);
    if (id) setHoveredId(id);
  };
  const blurNode = (event: FocusEvent<HTMLDivElement>) => {
    if (nodeIdOf(event.target)) setHoveredId(null);
  };
  const clickNode: NodeMouseHandler<Node<FlowNodeData>> = (_event, node) => onActivate(node.id);
  // React Flow focuses the node wrapper and only selects on Enter/Space internally; activate it the way a click does.
  // Keys on a control inside the card (the collapse button) keep their own meaning. Escape anywhere in the canvas
  // clears the selection, as a click on the empty canvas does.
  const keyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      // In full screen, Escape is the browser's way out; the selection and the view stay as they are.
      if (!document.fullscreenElement) onDeselect();
      return;
    }
    const id = nodeIdOf(event.target);
    if (!ACTIVATE_KEYS.has(event.key) || !id) return;
    event.preventDefault();
    onActivate(id);
  };
  const doubleClickNode: NodeMouseHandler<Node<FlowNodeData>> = (_event, node) => {
    if (node.data.node.kind === 'document') onOpenDocument(node.data.node.reconstructedDocumentId);
  };

  // In full screen (see ProjectGraph) the canvas fills the height beside the panel instead of 70vh.
  return (
    <div
      className="relative flex h-[70vh] min-h-[480px] flex-col overflow-hidden lg:flex-1 lg:self-start lg:[:fullscreen_&]:h-auto lg:[:fullscreen_&]:self-stretch rounded-lg border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900"
      onKeyDown={keyDown}
      onFocusCapture={focusNode}
      onBlurCapture={blurNode}
    >
      {help.shown && <GraphHelp onDismiss={help.dismiss} />}
      <div className="relative min-h-0 flex-1">
        <ReactFlow
          nodes={shown}
          edges={drawnEdges}
          nodeTypes={nodeTypes}
          colorMode={mode}
          onNodesChange={nodesChange}
          onNodeClick={clickNode}
          onNodeDoubleClick={doubleClickNode}
          onNodeMouseEnter={enterNode}
          onNodeMouseLeave={leaveNode}
          onPaneClick={onDeselect}
          className={CANVAS_THEME}
          fitView
          minZoom={0.02}
          nodesDraggable={false}
          nodesConnectable={false}
          zoomOnDoubleClick={false}
          proOptions={{ hideAttribution: true }}
        >
          <Reframe
            layoutKey={layoutKey}
            positions={positions}
            targets={targets}
            edges={flowEdges}
            moveMs={moveMs}
            mover={mover}
            request={viewportRequest}
          />
          <Background />
          <Controls showInteractive={false} />
          {/* Top left: the toolbar holds the top right and the zoom buttons the bottom left. At the bottom right
              the minimap covered ring-1 cards after a re-centre fit. */}
          <MiniMap
            position="top-left"
            pannable
            zoomable
            nodeColor={minimapColor}
            nodeStrokeWidth={0}
            className="hidden overflow-hidden rounded-lg border border-gray-200 shadow-sm sm:block dark:border-zinc-700 dark:shadow-zinc-950/30"
          />
          <CanvasToolbar
            mode="graph"
            onMode={onMode}
            helpShown={help.shown}
            onToggleHelp={help.shown ? help.dismiss : help.show}
            onCentre={centreOnFocus}
            onExportCsv={onExportCsv}
            fullscreen={fullscreen}
          />
          <FullscreenHold />
        </ReactFlow>
      </div>
      {error && <Overlay>Layout failed. Reload the page to try again.</Overlay>}
    </div>
  );
}
